import { KnowledgeNode } from '../../types/knowledge';
import { Conversation } from '../../types/conversation';
import { ConflictRecord, ConflictResolution, OfflineDBManager } from './offline-db-manager';
import DiffMatchPatch from 'diff-match-patch';

export interface MergeResult {
  merged: any;
  conflicts: MergeConflict[];
  autoResolved: boolean;
}

export interface MergeConflict {
  field: string;
  localValue: any;
  remoteValue: any;
  resolution?: any;
}

export class ConflictResolver {
  private dbManager: OfflineDBManager;
  private diffMatchPatch: DiffMatchPatch;

  constructor(dbManager: OfflineDBManager) {
    this.dbManager = dbManager;
    this.diffMatchPatch = new DiffMatchPatch();
  }

  async detectConflict(
    localData: any, 
    remoteData: any, 
    resourceType: string
  ): Promise<ConflictRecord | null> {
    const conflictType = this.analyzeConflictType(localData, remoteData);
    
    if (!conflictType) {
      return null; // No conflict
    }

    const conflict: ConflictRecord = {
      id: this.generateConflictId(),
      resourceType,
      resourceId: localData.id,
      localVersion: localData,
      remoteVersion: remoteData,
      conflictType,
      timestamp: new Date().toISOString(),
      status: 'pending',
      autoResolvable: this.isAutoResolvable(conflictType, localData, remoteData, resourceType),
    };

    await this.dbManager.storeConflict(conflict);
    return conflict;
  }

  async resolveConflict(
    conflictId: string, 
    strategy: ConflictResolution['strategy'], 
    userInput?: any
  ): Promise<any> {
    const conflict = await this.dbManager.getConflicts().then(conflicts => 
      conflicts.find(c => c.id === conflictId)
    );
    
    if (!conflict) {
      throw new Error('Conflict not found');
    }

    let resolvedData: any;

    switch (strategy) {
      case 'local':
        resolvedData = conflict.localVersion;
        break;
      
      case 'remote':
        resolvedData = conflict.remoteVersion;
        break;
      
      case 'merge':
        resolvedData = await this.performIntelligentMerge(conflict);
        break;
      
      case 'manual':
        if (!userInput) {
          throw new Error('Manual resolution requires user input');
        }
        resolvedData = userInput;
        break;
      
      default:
        throw new Error(`Unknown resolution strategy: ${strategy}`);
    }

    // Apply resolution
    await this.applyResolution(conflict, resolvedData, strategy);
    
    return resolvedData;
  }

  private analyzeConflictType(local: any, remote: any): ConflictRecord['conflictType'] | null {
    // Check if one was deleted
    if (local.deleted || remote.deleted) {
      return 'delete';
    }

    // Check for permission conflicts
    if (local.permissions && remote.permissions) {
      const localPerms = JSON.stringify(local.permissions);
      const remotePerms = JSON.stringify(remote.permissions);
      if (localPerms !== remotePerms) {
        return 'permission';
      }
    }

    // Check for schema conflicts (different structure)
    const localKeys = Object.keys(local).sort();
    const remoteKeys = Object.keys(remote).sort();
    if (JSON.stringify(localKeys) !== JSON.stringify(remoteKeys)) {
      return 'schema';
    }

    // Check for edit conflicts
    for (const key of localKeys) {
      if (key === 'id' || key === 'cachedAt' || key === 'offlineModified') {
        continue; // Skip metadata fields
      }
      
      if (JSON.stringify(local[key]) !== JSON.stringify(remote[key])) {
        return 'edit';
      }
    }

    return null; // No conflict
  }

  private isAutoResolvable(
    conflictType: ConflictRecord['conflictType'],
    local: any,
    remote: any,
    resourceType: string
  ): boolean {
    // Delete conflicts require manual resolution
    if (conflictType === 'delete' || conflictType === 'permission') {
      return false;
    }

    // Schema conflicts are complex
    if (conflictType === 'schema') {
      return false;
    }

    // Edit conflicts might be auto-resolvable
    if (conflictType === 'edit') {
      if (resourceType === 'knowledge') {
        return this.isKnowledgeNodeAutoResolvable(local, remote);
      } else if (resourceType === 'conversation') {
        return this.isConversationAutoResolvable(local, remote);
      }
    }

    return false;
  }

  private isKnowledgeNodeAutoResolvable(local: KnowledgeNode, remote: KnowledgeNode): boolean {
    const conflictingFields = this.getConflictingFields(local, remote);
    
    // Auto-resolvable if conflicts are only in certain fields
    const autoResolvableFields = ['tags', 'lastModified', 'viewCount'];
    const nonAutoResolvableConflicts = conflictingFields.filter(
      field => !autoResolvableFields.includes(field)
    );
    
    // If content differs, check if one is a superset of the other
    if (conflictingFields.includes('content')) {
      const localContent = local.content || '';
      const remoteContent = remote.content || '';
      
      // Simple check - if one contains the other
      if (localContent.includes(remoteContent) || remoteContent.includes(localContent)) {
        return true;
      }
      
      // Check if changes are in different parts of the content
      const diffs = this.diffMatchPatch.diff_main(localContent, remoteContent);
      const hasOverlappingChanges = this.hasOverlappingChanges(diffs);
      
      if (!hasOverlappingChanges) {
        return true;
      }
    }
    
    return nonAutoResolvableConflicts.length === 0;
  }

  private isConversationAutoResolvable(local: Conversation, remote: Conversation): boolean {
    const conflictingFields = this.getConflictingFields(local, remote);
    
    // Messages can be merged by timestamp
    if (conflictingFields.includes('messages')) {
      return true;
    }
    
    // Participant lists can be merged
    if (conflictingFields.includes('participants')) {
      return true;
    }
    
    // Title/description conflicts need manual resolution
    if (conflictingFields.includes('title') || conflictingFields.includes('description')) {
      return false;
    }
    
    return true;
  }

  private getConflictingFields(local: any, remote: any): string[] {
    const conflicts: string[] = [];
    
    for (const key of Object.keys(local)) {
      if (key === 'id' || key === 'cachedAt' || key === 'offlineModified') {
        continue; // Skip metadata
      }
      
      if (JSON.stringify(local[key]) !== JSON.stringify(remote[key])) {
        conflicts.push(key);
      }
    }
    
    return conflicts;
  }

  private async performIntelligentMerge(conflict: ConflictRecord): Promise<any> {
    const { localVersion, remoteVersion, resourceType } = conflict;

    if (resourceType === 'knowledge') {
      return this.mergeKnowledgeNode(localVersion, remoteVersion);
    } else if (resourceType === 'conversation') {
      return this.mergeConversation(localVersion, remoteVersion);
    }

    // Default merge strategy
    return this.performThreeWayMerge(localVersion, remoteVersion);
  }

  private mergeKnowledgeNode(local: KnowledgeNode, remote: KnowledgeNode): KnowledgeNode {
    const merged: KnowledgeNode = { ...remote }; // Start with remote as base

    // Merge content intelligently
    if (local.content !== remote.content) {
      merged.content = this.mergeTextContent(local.content || '', remote.content || '');
    }

    // Merge tags (union)
    if (local.tags || remote.tags) {
      merged.tags = Array.from(new Set([...(local.tags || []), ...(remote.tags || [])]));
    }

    // Use newer metadata
    if (local.lastModified && remote.lastModified) {
      merged.lastModified = new Date(local.lastModified) > new Date(remote.lastModified) 
        ? local.lastModified 
        : remote.lastModified;
    } else {
      merged.lastModified = local.lastModified || remote.lastModified;
    }

    // Merge relationships (union with conflict detection)
    if (local.relationships || remote.relationships) {
      merged.relationships = this.mergeRelationships(
        local.relationships || [],
        remote.relationships || []
      );
    }

    // Merge custom metadata
    if (local.metadata || remote.metadata) {
      merged.metadata = this.mergeMetadata(local.metadata, remote.metadata);
    }

    return merged;
  }

  private mergeConversation(local: Conversation, remote: Conversation): Conversation {
    const merged: Conversation = { ...remote };

    // Merge participants (union)
    if (local.participants || remote.participants) {
      const participantIds = new Set([
        ...(local.participants || []).map(p => p.id),
        ...(remote.participants || []).map(p => p.id),
      ]);
      
      // Keep unique participants
      merged.participants = Array.from(participantIds).map(id => {
        const localParticipant = local.participants?.find(p => p.id === id);
        const remoteParticipant = remote.participants?.find(p => p.id === id);
        return remoteParticipant || localParticipant!;
      });
    }

    // Use the most recent activity timestamp
    if (local.lastActivity && remote.lastActivity) {
      merged.lastActivity = new Date(local.lastActivity) > new Date(remote.lastActivity)
        ? local.lastActivity
        : remote.lastActivity;
    }

    return merged;
  }

  private mergeTextContent(localContent: string, remoteContent: string): string {
    // If one is empty, use the other
    if (!localContent) return remoteContent;
    if (!remoteContent) return localContent;
    
    // If they're the same, no conflict
    if (localContent === remoteContent) return localContent;
    
    // Check if one is a subset of the other
    if (localContent.includes(remoteContent)) return localContent;
    if (remoteContent.includes(localContent)) return remoteContent;
    
    // Use diff-match-patch for three-way merge
    const diffs = this.diffMatchPatch.diff_main(localContent, remoteContent);
    
    // Try to apply both changes if they don't overlap
    if (!this.hasOverlappingChanges(diffs)) {
      const patches = this.diffMatchPatch.patch_make(localContent, diffs);
      const [mergedContent, results] = this.diffMatchPatch.patch_apply(patches, localContent);
      
      // Check if all patches applied successfully
      if (results.every(r => r)) {
        return mergedContent;
      }
    }
    
    // If automatic merge fails, create a conflict marker
    return this.createConflictMarkers(localContent, remoteContent);
  }

  private hasOverlappingChanges(diffs: Array<[number, string]>): boolean {
    let hasAddition = false;
    let hasDeletion = false;
    
    for (const [operation] of diffs) {
      if (operation === 1) hasAddition = true; // Addition
      if (operation === -1) hasDeletion = true; // Deletion
    }
    
    // Overlapping if both additions and deletions exist
    return hasAddition && hasDeletion;
  }

  private createConflictMarkers(local: string, remote: string): string {
    return `<<<<<<< LOCAL\n${local}\n=======\n${remote}\n>>>>>>> REMOTE`;
  }

  private mergeRelationships(local: any[], remote: any[]): any[] {
    const relationshipMap = new Map<string, any>();
    
    // Add all remote relationships
    for (const rel of remote) {
      relationshipMap.set(rel.id, rel);
    }
    
    // Add/update with local relationships
    for (const rel of local) {
      const existing = relationshipMap.get(rel.id);
      if (existing) {
        // Merge if both exist - use newer
        const localDate = new Date(rel.updatedAt || rel.createdAt);
        const remoteDate = new Date(existing.updatedAt || existing.createdAt);
        if (localDate > remoteDate) {
          relationshipMap.set(rel.id, rel);
        }
      } else {
        relationshipMap.set(rel.id, rel);
      }
    }
    
    return Array.from(relationshipMap.values());
  }

  private mergeMetadata(local: any, remote: any): any {
    if (!local) return remote;
    if (!remote) return local;
    
    const merged = { ...remote };
    
    // Add fields from local that don't exist in remote
    for (const [key, value] of Object.entries(local)) {
      if (!(key in merged)) {
        merged[key] = value;
      }
    }
    
    return merged;
  }

  private performThreeWayMerge(local: any, remote: any): any {
    // Generic three-way merge
    const merged = { ...remote };
    
    for (const key of Object.keys(local)) {
      if (!(key in remote)) {
        // Field only in local
        merged[key] = local[key];
      } else if (JSON.stringify(local[key]) !== JSON.stringify(remote[key])) {
        // Conflict - use type-specific merge
        if (Array.isArray(local[key]) && Array.isArray(remote[key])) {
          // Merge arrays (union)
          merged[key] = Array.from(new Set([...local[key], ...remote[key]]));
        } else if (typeof local[key] === 'object' && typeof remote[key] === 'object') {
          // Recursively merge objects
          merged[key] = this.performThreeWayMerge(local[key], remote[key]);
        } else if (typeof local[key] === 'string' && typeof remote[key] === 'string') {
          // Try to merge strings
          merged[key] = this.mergeTextContent(local[key], remote[key]);
        } else {
          // Use newer value based on timestamp
          if (local.lastModified && remote.lastModified) {
            merged[key] = new Date(local.lastModified) > new Date(remote.lastModified)
              ? local[key]
              : remote[key];
          } else {
            // Default to remote
            merged[key] = remote[key];
          }
        }
      }
    }
    
    return merged;
  }

  private async applyResolution(
    conflict: ConflictRecord,
    resolvedData: any,
    strategy: ConflictResolution['strategy']
  ): Promise<void> {
    // Update conflict record
    await this.dbManager.updateConflictResolution(conflict.id, {
      strategy,
      mergedData: strategy === 'merge' || strategy === 'manual' ? resolvedData : undefined,
      timestamp: new Date().toISOString(),
    });
    
    // Update local cache with resolved data
    switch (conflict.resourceType) {
      case 'knowledge':
        await this.dbManager.storeKnowledgeNode(resolvedData);
        break;
      case 'conversation':
        await this.dbManager.storeConversation(resolvedData);
        break;
      // Add other resource types
    }
  }

  async getConflictSummary(conflict: ConflictRecord): Promise<{
    fields: MergeConflict[];
    suggestion: string;
    autoResolvable: boolean;
  }> {
    const fields = this.compareVersions(conflict.localVersion, conflict.remoteVersion);
    const suggestion = this.generateResolutionSuggestion(fields, conflict.resourceType);
    
    return {
      fields,
      suggestion,
      autoResolvable: conflict.autoResolvable,
    };
  }

  private compareVersions(local: any, remote: any): MergeConflict[] {
    const conflicts: MergeConflict[] = [];
    const allKeys = Array.from(new Set([...Object.keys(local), ...Object.keys(remote)]));
    
    for (const key of allKeys) {
      if (key === 'id' || key === 'cachedAt' || key === 'offlineModified') {
        continue; // Skip metadata
      }
      
      const localValue = local[key];
      const remoteValue = remote[key];
      
      if (JSON.stringify(localValue) !== JSON.stringify(remoteValue)) {
        conflicts.push({
          field: key,
          localValue,
          remoteValue,
        });
      }
    }
    
    return conflicts;
  }

  private generateResolutionSuggestion(conflicts: MergeConflict[], resourceType: string): string {
    if (conflicts.length === 0) {
      return 'No conflicts detected.';
    }
    
    const conflictFields = conflicts.map(c => c.field);
    
    if (resourceType === 'knowledge') {
      if (conflictFields.includes('content')) {
        return 'Content has been modified in both versions. Review the changes and choose the appropriate version or merge them manually.';
      }
      if (conflictFields.every(f => ['tags', 'metadata'].includes(f))) {
        return 'Only metadata conflicts detected. These can be automatically merged.';
      }
    }
    
    if (conflictFields.length === 1) {
      return `Only the "${conflictFields[0]}" field has conflicts. You can choose which version to keep.`;
    }
    
    return `${conflicts.length} fields have conflicts. Review each change carefully before resolving.`;
  }

  private generateConflictId(): string {
    return `conflict-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }
}